// Character Classes
export enum CharacterClass {
  FIGHTER = 0,
  THIEF = 1,
  MONK = 2,
  RED_MAGE = 3,
  WHITE_MAGE = 4,
  BLACK_MAGE = 5,
}

// Status Effects (bitflags)
export enum StatusEffect {
  NONE = 0,
  POISON = 1 << 0,
  STONE = 1 << 1,
  DEAD = 1 << 2,
  PARALYZED = 1 << 3,
  SLEEPING = 1 << 4,
  SILENCED = 1 << 5,
  BLINDED = 1 << 6,
  CONFUSED = 1 << 7,
}

// Elemental Types
export enum Element {
  NONE = 0,
  FIRE = 1,
  ICE = 2,
  LIGHTNING = 3,
  EARTH = 4,
  WIND = 5,
  WATER = 6,
  HOLY = 7,
  DARK = 8,
}

// Map Types
export enum MapType {
  WORLD = 0,
  TOWN = 1,
  DUNGEON = 2,
  CASTLE = 3,
}

// Character struct matching Solidity
export interface Character {
  class: CharacterClass;
  level: number;
  experience: number;
  currentHP: number;
  maxHP: number;
  currentMP: number;
  maxMP: number;
  strength: number;
  agility: number;
  intelligence: number;
  vitality: number;
  luck: number;
  weapon: number;
  armor: number;
  helmet: number;
  gloves: number;
  accessory: number;
  status: number; // Bitflags
}

// Game State struct matching Solidity
export interface GameState {
  party: [Character, Character, Character, Character];
  worldX: number;
  worldY: number;
  currentMap: number;
  dungeonFloor: number;
  facing: number; // 0=N, 1=E, 2=S, 3=W
  gil: number; // In-game currency (Gil)
  stepsToEncounter: number;
  storyFlags: bigint; // 256 bits
  inventory: number[]; // 64 items
  inventoryQty: number[]; // 64 quantities
  keyItems: number; // 32 bits
  playTime: number; // Blocks since game start
  startBlock: number; // Block when game started
}

// Combat State struct matching Solidity
export interface CombatState {
  active: boolean;
  enemyIds: number[]; // 8 enemies
  enemyHP: number[]; // 8 HP values
  turnQueue: number[]; // 12 turn order (4 party + 8 enemies)
  currentTurn: number;
  canFlee: boolean;
}

// Item definition
export interface Item {
  id: number;
  name: string;
  description: string;
  type: ItemType;
  power: number;
  defense: number;
  element: Element;
  price: number;
  equipSlot?: EquipSlot;
  usableInCombat: boolean;
  usableOutside: boolean;
}

export enum ItemType {
  WEAPON = 0,
  ARMOR = 1,
  HELMET = 2,
  GLOVES = 3,
  ACCESSORY = 4,
  CONSUMABLE = 5,
  KEY_ITEM = 6,
}

export enum EquipSlot {
  WEAPON = 0,
  ARMOR = 1,
  HELMET = 2,
  GLOVES = 3,
  ACCESSORY = 4,
}

// Spell definition
export interface Spell {
  id: number;
  name: string;
  description: string;
  level: number; // 1-8
  type: SpellType;
  element: Element;
  power: number;
  target: SpellTarget;
  statusEffect?: StatusEffect;
}

export enum SpellType {
  DAMAGE = 0,
  HEALING = 1,
  BUFF = 2,
  DEBUFF = 3,
  STATUS = 4,
}

export enum SpellTarget {
  SINGLE_ENEMY = 0,
  ALL_ENEMIES = 1,
  SINGLE_ALLY = 2,
  ALL_ALLIES = 3,
  SELF = 4,
}

// Enemy definition
export interface Enemy {
  id: number;
  name: string;
  level: number;
  hp: number;
  strength: number;
  defense: number;
  agility: number;
  intelligence: number;
  magicDefense: number;
  exp: number;
  gil: number;
  element: Element;
  weaknesses: Element[];
  resistances: Element[];
  abilities: number[]; // Spell IDs
}

// Combat action types
export enum CombatAction {
  ATTACK = 0,
  MAGIC = 1,
  ITEM = 2,
  FLEE = 3,
  DEFEND = 4,
}

// Transaction status
export interface TransactionStatus {
  hash: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
}

// Class display info with base stats (FF1-authentic starting values)
export const CLASS_INFO: Record<CharacterClass, {
  name: string;
  description: string;
  baseHP: number;
  baseMP: number;
  baseStr: number;
  baseAgi: number;
  baseInt: number;
  baseVit: number;
  baseLuck: number;
}> = {
  [CharacterClass.FIGHTER]: {
    name: 'Fighter',
    description: 'High HP and strength. Can equip heavy armor and powerful weapons.',
    baseHP: 35,
    baseMP: 0,
    baseStr: 20,
    baseAgi: 5,
    baseInt: 1,
    baseVit: 10,
    baseLuck: 5,
  },
  [CharacterClass.THIEF]: {
    name: 'Thief',
    description: 'High agility and luck. Improved flee rate and critical hits.',
    baseHP: 30,
    baseMP: 0,
    baseStr: 10,
    baseAgi: 15,
    baseInt: 5,
    baseVit: 5,
    baseLuck: 15,
  },
  [CharacterClass.MONK]: {
    name: 'Monk',
    description: 'Powerful bare-handed attacks. High HP but limited equipment.',
    baseHP: 33,
    baseMP: 0,
    baseStr: 12,
    baseAgi: 8,
    baseInt: 3,
    baseVit: 15,
    baseLuck: 8,
  },
  [CharacterClass.RED_MAGE]: {
    name: 'Red Mage',
    description: 'Balanced stats. Can use both White and Black magic up to level 6.',
    baseHP: 30,
    baseMP: 10,
    baseStr: 10,
    baseAgi: 10,
    baseInt: 10,
    baseVit: 8,
    baseLuck: 10,
  },
  [CharacterClass.WHITE_MAGE]: {
    name: 'White Mage',
    description: 'Healing and support magic specialist. Low HP and defense.',
    baseHP: 25,
    baseMP: 15,
    baseStr: 5,
    baseAgi: 8,
    baseInt: 15,
    baseVit: 5,
    baseLuck: 10,
  },
  [CharacterClass.BLACK_MAGE]: {
    name: 'Black Mage',
    description: 'Offensive magic specialist. High intelligence, low HP.',
    baseHP: 22,
    baseMP: 18,
    baseStr: 3,
    baseAgi: 10,
    baseInt: 20,
    baseVit: 3,
    baseLuck: 10,
  },
};

// Element display names
export const ELEMENT_NAMES: Record<Element, string> = {
  [Element.NONE]: 'None',
  [Element.FIRE]: 'Fire',
  [Element.ICE]: 'Ice',
  [Element.LIGHTNING]: 'Lightning',
  [Element.EARTH]: 'Earth',
  [Element.WIND]: 'Wind',
  [Element.WATER]: 'Water',
  [Element.HOLY]: 'Holy',
  [Element.DARK]: 'Dark',
};

// Status effect display names
export const STATUS_NAMES: Record<StatusEffect, string> = {
  [StatusEffect.NONE]: 'Normal',
  [StatusEffect.POISON]: 'Poison',
  [StatusEffect.STONE]: 'Stone',
  [StatusEffect.DEAD]: 'Dead',
  [StatusEffect.PARALYZED]: 'Paralyzed',
  [StatusEffect.SLEEPING]: 'Sleeping',
  [StatusEffect.SILENCED]: 'Silenced',
  [StatusEffect.BLINDED]: 'Blinded',
  [StatusEffect.CONFUSED]: 'Confused',
};
